import React, { useState, useEffect } from 'react'
import Select from 'react-select'
import { useDispatch, useSelector } from 'react-redux'
import { Formik } from 'formik'
import Button from '@mui/material/Button'
import * as yup from 'yup'
import { useNavigate, useParams } from 'react-router-dom'
import { alpha, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { toast } from 'react-toastify'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AppBar from '@mui/material/AppBar'
import Container from '../../GeneralComponents/Container'
import { Topbar, Sidebar } from '../components'
import setGlobalLoader from '../../../redux/actions/loaderActions'
import {
  getAgentById,
  updateAgent,
} from '../../../redux/actions/apiActions'
import { CircularProgress } from '@mui/material'

const validationSchema = yup.object({
  name: yup.string().trim().required('Name is required.'),
  phoneNumber: yup
    .string()
    .trim()
    .required('Phone Number is required.')
    .min(10, 'Phone number short than 10 digits.')
    .max(10, 'Phone number long than 10 digits.'),
  email: yup
    .string()
    .trim()
    .email('Invalid email format.')
    .nullable(),
  bankAccountHolderName: yup
    .string()
    .trim()
    .nullable(),
  bankIFSCCode: yup
    .string()
    .trim()
    .nullable(),
  bankAccountNumber: yup
    .string()
    .trim()
    .nullable()
})

const EditAgent = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const params = useParams()
  const [agentDetails, setAgentDetails] = useState([])
  const theme = useTheme()
  const role = useSelector(state => state.authReducer.role)
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  })

  const [openSidebar, setOpenSidebar] = useState(false)

  const handleSidebarOpen = () => {
    setOpenSidebar(true)
  }

  const handleSidebarClose = () => {
    setOpenSidebar(false)
  }
  const open = isMd ? false : openSidebar

  useEffect(() => {
    if (params.id !== null && params.id !== undefined) {
      dispatch(setGlobalLoader(true))
      async function agentFunc() {
        try {
          const agentRes = await getAgentById(params.id)
          setAgentDetails(agentRes?.data?.data)
          dispatch(setGlobalLoader(false))
        } catch (error) {
          dispatch(setGlobalLoader(false))
          console.log('get agent details error', error)
          toast.error(
            error.response.data.msg ??
            'some error occured while fetching agent details'
          )
        }
      }
      agentFunc()
    }
  }, [params.id])

  const handleOnSubmit = async values => {
    if (values.email === ''){
      values.email = null;
    }

    try {
      await updateAgent(params.id, values)
      toast.success('Agent updated successfully')
      navigate('/agents')
      dispatch(setGlobalLoader(false))
    } catch (error) {
      dispatch(setGlobalLoader(false))
      console.log('update agent error', error)
      toast.error(error.response.data.msg ?? 'Some error occured.')
    }
  }

  return (
    <Box>
      <AppBar
        position={'fixed'}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        elevation={0}
      >
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar onSidebarOpen={handleSidebarOpen} />
        </Container>
      </AppBar>
      <Sidebar
        onClose={handleSidebarClose}
        open={open}
        variant={isMd ? 'permanent' : 'temporary'}
      />
      <main>
        <Box height={{ xs: 94, sm: 102, md: 107 }} />
        <br />
        <br />

        <Box overflow="hidden" marginLeft={{ md: '270px' }}>
          <Typography
            variant={'subtitle2'}
            sx={{ marginBottom: 2 }}
            fontWeight={700}
          >
            Edit Agent
          </Typography>

          <Formik
            initialValues={{
              name: agentDetails?.name,
              phoneNumber: agentDetails?.phoneNumber,
              email: agentDetails?.email,
              bankAccountHolderName: agentDetails?.bankAccountHolderName,
              bankIFSCCode: agentDetails?.bankIFSCCode,
              bankAccountNumber: agentDetails?.bankAccountNumber,
            }}
            validationSchema={validationSchema}
            onSubmit={values => handleOnSubmit(values)}
            enableReinitialize
          >
            {formikProps => {
              const {
                values,
                errors,
                touched,
                setFieldValue,
                handleSubmit,
                isSubmitting,
              } = formikProps
              return (
                <form onSubmit={handleSubmit}>
                  <Grid
                    container
                    spacing={{ xs: 2, md: 4 }}
                    sx={{ padding: '10px' }}
                  >
                    <Grid item xs={12} sm={6} md={7}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 2 }}
                        fontWeight={700}
                      >
                        Name
                      </Typography>
                      <TextField
                        // label="Name *"
                        variant="outlined"
                        name={'name'}
                        fullWidth
                        value={values.name}
                        onChange={e => setFieldValue('name', e.target.value)}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={7}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 2 }}
                        fontWeight={700}
                      >
                        Phone Number
                      </Typography>
                      <TextField
                        // label="Phone Number *"
                        variant="outlined"
                        name={'phoneNumber'}
                        fullWidth
                        type="number"
                        value={values.phoneNumber}
                        onChange={e =>
                          e.target.value.toString().length > 10
                            ? setFieldValue(
                              'phoneNumber',
                              e.target.value.substring(0, 10)
                            )
                            : setFieldValue('phoneNumber', e.target.value)
                        }
                        error={
                          touched.phoneNumber && Boolean(errors.phoneNumber)
                        }
                        helperText={touched.phoneNumber && errors.phoneNumber}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={7}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 2 }}
                        fontWeight={700}
                      >
                        Email
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'email'}
                        fullWidth
                        type="email"
                        value={values.email}
                        onChange={e => setFieldValue('email', e.target.value)}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={7}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 2 }}
                        fontWeight={700}
                      >
                        Bank Account Holder Name
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'bankAccountHolderName'}
                        fullWidth
                        value={values.bankAccountHolderName}
                        onChange={e => setFieldValue('bankAccountHolderName', e.target.value)}
                        error={touched.bankAccountHolderName && Boolean(errors.bankAccountHolderName)}
                        helperText={touched.bankAccountHolderName && errors.bankAccountHolderName}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={7}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 2 }}
                        fontWeight={700}
                      >
                        Bank IFSC Code
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'bankIFSCCode'}
                        fullWidth
                        value={values.bankIFSCCode}
                        onChange={e => setFieldValue('bankIFSCCode', e.target.value)}
                        error={touched.bankIFSCCode && Boolean(errors.bankIFSCCode)}
                        helperText={touched.bankIFSCCode && errors.bankIFSCCode}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={7}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 2 }}
                        fontWeight={700}
                      >
                        Bank Account Number
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'bankAccountNumber'}
                        fullWidth
                        value={values.bankAccountNumber}
                        onChange={e => setFieldValue('bankAccountNumber', e.target.value)}
                        error={touched.bankAccountNumber && Boolean(errors.bankAccountNumber)}
                        helperText={touched.bankAccountNumber && errors.bankAccountNumber}
                      />
                    </Grid>
                    <br />
                  </Grid>
                  <br />
                  <Button
                    component={'button'}
                    variant={'contained'}
                    fontWeight={700}
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <CircularProgress /> : 'Submit'}
                  </Button>
                </form>
              )
            }}
          </Formik>
        </Box>
      </main>
    </Box>
  )
}

export default EditAgent